import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAction, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { CHUNK_INTERVAL_MS, MAX_UPLOAD_BYTES } from "@protocol";
import { errorMessage } from "@/lib/ui";
import { clearRecording, patchSession, putChunk, putSession } from "./chunkStore";
import {
  ChunkUploader,
  FILE_UPLOAD_WINDOW,
  FileChunkSource,
  type UploaderStatus,
} from "./uploader";
import { ScreenWakeLock } from "./wakeLock";
import { checkAudioFile, chunkBytesFor, probeDurationMs, uploadMimeType } from "./audioFile";
import { MAX_VIDEO_BYTES, extractMp3, isVideoFile } from "./extractAudio";
import { readFileTiming, startOf, type FileTiming } from "./fileTime";
import { joinToMp3, type JoinedAudio } from "./joinAudio";
import {
  INGEST_WS_URL,
  RecorderContext,
  isBusy,
  type AudioSource,
  type BatchChoice,
  type BatchFile,
  type PendingBatch,
  type RecorderApi,
  type RecorderMode,
  type RecorderStatus,
  type UploadState,
} from "./context";

/**
 * The capture side of PLAN.md §7.
 *
 * Ordering is the whole design here: the Convex row exists before the first
 * blob, every blob reaches IndexedDB before the uploader is told about it, and
 * finalize is only requested once the last blob is written. Nothing in this
 * file ever holds audio only in memory.
 */

/** Chrome and Firefox take the first, Safari falls through to mp4/AAC. */
const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

const SOURCE_KEY = "lls.audioSource";

/**
 * Tab/system audio only exists through a screen share, and no mobile browser
 * exposes `getDisplayMedia` - Android Chrome and iOS Safari both leave it out.
 */
function detectSystemAudio(): boolean {
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.mediaDevices?.getDisplayMedia !== "function") return false;
  return !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function loadSource(): AudioSource {
  try {
    return localStorage.getItem(SOURCE_KEY) === "system" ? "system" : "mic";
  } catch {
    return "mic";
  }
}

async function acquireMic(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: false,
      // Noise suppression hurts ASR on lecture-hall audio (PLAN.md §7).
      noiseSuppression: false,
      autoGainControl: true,
    },
  });
}

/**
 * Chrome only hands out audio alongside a video share, so the video track is
 * requested and dropped at once. `systemAudio` lets a whole-screen share carry
 * the machine's output on Windows/ChromeOS; a tab share carries the tab's.
 */
async function acquireSystemAudio(): Promise<MediaStream> {
  const options = {
    video: true,
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    systemAudio: "include",
    selfBrowserSurface: "exclude",
  } as DisplayMediaStreamOptions;
  const shared = await navigator.mediaDevices.getDisplayMedia(options);
  shared.getVideoTracks().forEach((track) => track.stop());
  const audio = shared.getAudioTracks();
  if (audio.length === 0) {
    throw new Error('No audio was shared - pick a tab or screen and turn on "Share audio".');
  }
  return new MediaStream(audio);
}

/** "Lecture.m4a" and two more become "Lecture (3 parts).mp3". */
function joinedName(first: string, count: number): string {
  return `${first.replace(/\.[^.]+$/, "")} (${count} parts).mp3`;
}

export default function RecorderProvider({ children }: { children: ReactNode }) {
  const startRecording = useMutation(api.recordings.start);
  const startUpload = useMutation(api.recordings.startUpload);
  const markStopped = useMutation(api.recordings.markStopped);
  const markFailed = useMutation(api.recordings.markFailed);
  const removeRecording = useMutation(api.recordings.remove);
  const issueToken = useAction(api.recordings.issueIngestToken);

  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [recordingId, setRecordingId] = useState<Id<"recordings"> | null>(null);
  const [lessonId, setLessonId] = useState<Id<"lessons"> | null>(null);
  const [pending, setPending] = useState(0);
  const [connection, setConnection] = useState<UploaderStatus>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState(false);
  const [systemAudioSupported] = useState(detectSystemAudio);
  const [chosenSource, setChosenSource] = useState<AudioSource>(loadSource);
  const source: AudioSource = systemAudioSupported ? chosenSource : "mic";
  const [mode, setMode] = useState<RecorderMode>("capture");
  const [upload, setUpload] = useState<UploadState | null>(null);
  const [batch, setBatch] = useState<PendingBatch | null>(null);

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const uploader = useRef<ChunkUploader | null>(null);
  const wakeLock = useRef<ScreenWakeLock>(new ScreenWakeLock());
  const seq = useRef(0);
  const startedAt = useRef(0);
  /** Serializes chunk writes so `stop` can wait for the last one. */
  const writes = useRef<Promise<void>>(Promise.resolve());
  /** Aborts the in-browser MP3 extraction of a dropped video. */
  const extraction = useRef<AbortController | null>(null);
  /** Resolves on the recorder's `stop` event, whoever caused it. */
  const stopped = useRef<Promise<void>>(Promise.resolve());
  /** Latest `stop`, for track `ended` handlers bound at start time. */
  const stopRef = useRef<() => Promise<void>>(async () => undefined);
  /** Resolves the running file upload's promise; `cancel` settles it as failed. */
  const settle = useRef<((stored: boolean) => void) | null>(null);
  /** Bumped when a batch is answered, so a slow probe cannot reopen it. */
  const batchToken = useRef(0);

  const setSource = useCallback((next: AudioSource) => {
    setChosenSource(next);
    try {
      localStorage.setItem(SOURCE_KEY, next);
    } catch {
      // Private mode: the choice just will not survive a reload.
    }
  }, []);

  const releaseDevice = useCallback(() => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    mediaRecorder.current = null;
    void wakeLock.current.disable();
  }, []);

  /** The socket's state, plus the one step of it the controls show on their own. */
  const trackConnection = useCallback((next: UploaderStatus) => {
    setConnection(next);
    if (next === "finalizing") {
      setStatus((current) => (current === "uploading" ? "finalizing" : current));
    }
  }, []);

  const start = useCallback(
    async (targetLessonId: Id<"lessons"> | null = null) => {
      if (isBusy(status) || status === "requesting") return;
      if (INGEST_WS_URL === "") {
        setError("VITE_INGEST_WS_URL is not configured.");
        setStatus("error");
        return;
      }
      setError(null);
      setPartial(false);
      setMode("capture");
      setUpload(null);
      setStatus("requesting");

      let media: MediaStream;
      try {
        media = source === "system" ? await acquireSystemAudio() : await acquireMic();
      } catch (caught) {
        setError(
          caught instanceof Error
            ? caught.message
            : source === "system"
              ? "Audio sharing was cancelled."
              : "Microphone access was denied.",
        );
        setStatus("error");
        return;
      }

      const preferred = pickMimeType();
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(
          media,
          preferred === undefined ? undefined : { mimeType: preferred },
        );
      } catch (caught) {
        media.getTracks().forEach((track) => track.stop());
        setError(caught instanceof Error ? caught.message : "MediaRecorder failed.");
        setStatus("error");
        return;
      }

      // `recorder.mimeType` is what the browser actually chose, which may differ
      // from what was asked for. The container has to be honest for ffmpeg.
      const mimeType = recorder.mimeType !== "" ? recorder.mimeType : (preferred ?? "audio/webm");
      const deviceLabel = media.getAudioTracks()[0]?.label || undefined;

      let id: Id<"recordings">;
      try {
        id = await startRecording({
          lessonId: targetLessonId ?? undefined,
          mimeType,
          deviceLabel,
        });
      } catch (caught) {
        media.getTracks().forEach((track) => track.stop());
        setError(caught instanceof Error ? caught.message : "Could not start recording.");
        setStatus("error");
        return;
      }

      await putSession({
        recordingId: id,
        lessonId: targetLessonId,
        mimeType,
        startedAt: Date.now(),
        stopRequested: false,
        finalized: false,
        nextSeq: 0,
      });

      seq.current = 0;
      startedAt.current = Date.now();
      writes.current = Promise.resolve();
      stream.current = media;
      mediaRecorder.current = recorder;
      setRecordingId(id);
      setLessonId(targetLessonId);
      setPending(0);

      const upload = new ChunkUploader({
        recordingId: id,
        mimeType,
        wsUrl: INGEST_WS_URL,
        getToken: async () => (await issueToken({ id })).token,
        onStatus: trackConnection,
        onPending: setPending,
        onFinalized: (result) => {
          setPartial(result.gapSeqs.length > 0);
          setStatus("done");
          setPending(0);
        },
        onFatal: (message) => {
          setError(message);
          setStatus("error");
        },
      });
      uploader.current = upload;
      upload.start();

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size === 0) return;
        const chunkSeq = seq.current;
        seq.current += 1;
        const blob = event.data;
        // Chained so the write order matches the seq order and `stop` can await
        // the tail of the chain.
        writes.current = writes.current
          .then(async () => {
            await putChunk({
              recordingId: id,
              seq: chunkSeq,
              clientTs: Date.now(),
              blob,
              bytes: blob.size,
            });
            await patchSession(id, { nextSeq: chunkSeq + 1 });
            upload.notify();
          })
          .catch((caught: unknown) => {
            // A failed IndexedDB write is the one unrecoverable case: the bytes
            // exist nowhere else. Surface it instead of silently dropping audio.
            setError(
              `Could not save audio locally: ${
                caught instanceof Error ? caught.message : String(caught)
              }`,
            );
            setStatus("error");
          });
      };

      stopped.current = new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
      });

      // "Stop sharing" in the browser bar, or an unplugged mic, ends the track
      // and the recorder with it - treat that as a normal stop so it uploads.
      media.getAudioTracks().forEach((track) => {
        track.onended = () => void stopRef.current();
      });

      recorder.onerror = () => {
        setError("The browser stopped the recording.");
        setStatus("error");
      };

      recorder.start(CHUNK_INTERVAL_MS);
      void wakeLock.current.enable();
      setStatus("recording");
    },
    [issueToken, source, startRecording, status, trackConnection],
  );

  /**
   * One file through the upload path. Resolves true once ingest has finalized
   * it and false when it failed or was cancelled, so a batch only moves on to
   * the next part after this one is stored.
   */
  const runUpload = useCallback(
    async (
      dropped: File,
      targetLessonId: Id<"lessons"> | null,
      options: { part?: UploadState["part"]; last?: boolean; timing?: FileTiming } = {},
    ): Promise<boolean> => {
      const part = options.part ?? null;
      const last = options.last ?? true;
      // A finished run's socket is already closed; this only drops the handle.
      uploader.current?.dispose();
      uploader.current = null;
      const video = isVideoFile(dropped);
      setMode("upload");
      setUpload({
        fileName: dropped.name,
        totalBytes: dropped.size,
        phase: video ? "extracting" : "uploading",
        progress: 0,
        part,
      });
      setRecordingId(null);
      setLessonId(targetLessonId);
      if (part === null || part.index === 0) setPartial(false);
      setPending(0);
      setElapsedMs(0);
      setConnection("idle");

      const problem =
        INGEST_WS_URL === ""
          ? "VITE_INGEST_WS_URL is not configured."
          : checkAudioFile(dropped, video ? MAX_VIDEO_BYTES : MAX_UPLOAD_BYTES);
      if (problem !== null) {
        setError(problem);
        setStatus("error");
        return false;
      }
      setError(null);
      setStatus("requesting");

      // Read before a video is converted: the MP3 copy carries none of its tags.
      const timing = options.timing ?? (await readFileTiming(dropped));

      // A video's soundtrack is all the pipeline uses, so only that is sent.
      let file = dropped;
      if (video) {
        const controller = new AbortController();
        extraction.current = controller;
        try {
          file = await extractMp3(
            dropped,
            (progress) =>
              setUpload((current) =>
                current?.phase === "extracting" ? { ...current, progress } : current,
              ),
            controller.signal,
          );
        } catch (caught) {
          // Cancelled: `cancel` has already put everything back to idle.
          if (controller.signal.aborted) return false;
          // This browser could not convert it; the worker's ffmpeg still can,
          // as long as the whole video fits under the upload limit.
          if (dropped.size > MAX_UPLOAD_BYTES) {
            setError(
              caught instanceof Error
                ? caught.message
                : "Could not take the audio out of that video.",
            );
            setStatus("error");
            return false;
          }
        } finally {
          if (extraction.current === controller) extraction.current = null;
        }
        setUpload({
          fileName: file.name,
          totalBytes: file.size,
          phase: "uploading",
          progress: 0,
          part,
        });
      }

      // Header-only read; it decides where the file sits in time for matching.
      const durationMs = timing.durationMs ?? (await probeDurationMs(file));
      const mimeType = uploadMimeType(file);
      let id: Id<"recordings">;
      try {
        id = await startUpload({
          lessonId: targetLessonId ?? undefined,
          mimeType,
          fileName: file.name,
          fileBytes: file.size,
          durationMs: durationMs ?? undefined,
          fileModifiedAt: file.lastModified > 0 ? file.lastModified : undefined,
          recordedAt: timing.recordedAt ?? undefined,
        });
      } catch (caught) {
        setError(errorMessage(caught, "Could not start the upload."));
        setStatus("error");
        return false;
      }

      const fileSource = new FileChunkSource(file, chunkBytesFor(file.size));
      // Only the file's identity is kept locally, never its bytes. Without
      // IndexedDB the upload still runs; it just cannot resume after a reload.
      await putSession({
        recordingId: id,
        lessonId: targetLessonId,
        mimeType,
        startedAt: Date.now(),
        stopRequested: true,
        finalized: false,
        nextSeq: fileSource.chunkCount,
        label: file.name,
        upload: {
          fileName: file.name,
          fileBytes: file.size,
          lastModified: file.lastModified,
          chunkBytes: fileSource.chunkBytes,
        },
      }).catch(() => undefined);

      setRecordingId(id);
      setPending(fileSource.chunkCount);
      setElapsedMs(durationMs ?? 0);
      setStatus("uploading");
      // A phone that sleeps suspends the tab, and the upload with it.
      void wakeLock.current.enable();

      return new Promise<boolean>((resolve) => {
        const finish = (stored: boolean) => {
          if (settle.current === finish) settle.current = null;
          resolve(stored);
        };
        settle.current = finish;
        const send = new ChunkUploader({
          recordingId: id,
          mimeType,
          wsUrl: INGEST_WS_URL,
          getToken: async () => (await issueToken({ id })).token,
          source: fileSource,
          maxInFlight: FILE_UPLOAD_WINDOW,
          onStatus: trackConnection,
          onPending: (left) => {
            setPending(left);
            setUpload((current) =>
              current === null
                ? current
                : { ...current, progress: 1 - left / fileSource.chunkCount },
            );
          },
          onFinalized: (result) => {
            if (result.gapSeqs.length > 0) setPartial(true);
            setUpload((current) => (current === null ? current : { ...current, progress: 1 }));
            setPending(0);
            // A batch goes straight on to its next part.
            if (last) setStatus("done");
            void wakeLock.current.disable();
            void clearRecording(id).catch(() => undefined);
            finish(true);
          },
          onFatal: (message) => {
            setError(message);
            setStatus("error");
            void wakeLock.current.disable();
            finish(false);
          },
        });
        uploader.current = send;
        send.start();
        send.requestFinalize();
      });
    },
    [issueToken, startUpload, trackConnection],
  );

  /** Joins the parts into one MP3 in the browser, then uploads that. */
  const runJoin = useCallback(
    async (parts: BatchFile[], targetLessonId: Id<"lessons"> | null) => {
      uploader.current?.dispose();
      uploader.current = null;
      const name = joinedName(parts[0].file.name, parts.length);
      setMode("upload");
      setUpload({
        fileName: name,
        totalBytes: parts.reduce((sum, part) => sum + part.file.size, 0),
        phase: "joining",
        progress: 0,
        part: null,
      });
      setRecordingId(null);
      setLessonId(targetLessonId);
      setPartial(false);
      setPending(0);
      setElapsedMs(0);
      setConnection("idle");
      setError(null);
      setStatus("requesting");

      const controller = new AbortController();
      extraction.current = controller;
      let joined: JoinedAudio;
      try {
        joined = await joinToMp3(
          parts.map((part) => part.file),
          name,
          (progress) =>
            setUpload((current) =>
              current?.phase === "joining" ? { ...current, progress } : current,
            ),
          controller.signal,
        );
      } catch (caught) {
        if (controller.signal.aborted) return;
        setError(
          `${caught instanceof Error ? caught.message : "Could not join the files."} ` +
            "Upload them as separate recordings instead.",
        );
        setStatus("error");
        return;
      } finally {
        if (extraction.current === controller) extraction.current = null;
      }

      // The joined file is new, so its own times say nothing: it starts when
      // the first part did.
      await runUpload(joined.file, targetLessonId, {
        timing: { recordedAt: parts[0].startMs, durationMs: joined.durationMs },
      });
    },
    [runUpload],
  );

  const uploadFiles = useCallback(
    async (files: File[], targetLessonId: Id<"lessons"> | null = null) => {
      if (isBusy(status) || status === "requesting" || batch !== null) return;
      if (files.length === 0) return;
      if (files.length === 1) {
        await runUpload(files[0], targetLessonId);
        return;
      }

      const problem =
        INGEST_WS_URL === ""
          ? "VITE_INGEST_WS_URL is not configured."
          : (files
              .map((file) =>
                checkAudioFile(file, isVideoFile(file) ? MAX_VIDEO_BYTES : MAX_UPLOAD_BYTES),
              )
              .find((reason) => reason !== null) ?? null);
      if (problem !== null) {
        setMode("upload");
        setUpload(null);
        setError(problem);
        setStatus("error");
        return;
      }
      setError(null);

      const token = ++batchToken.current;
      const unknown: FileTiming = { recordedAt: null, durationMs: null };
      setBatch({
        files: files.map((file) => ({
          file,
          timing: unknown,
          startMs: file.lastModified,
          fromFile: false,
        })),
        lessonId: targetLessonId,
        probing: true,
      });

      const probed = await Promise.all(
        files.map(async (file): Promise<BatchFile> => {
          const read = await readFileTiming(file).catch(() => unknown);
          const timing = {
            ...read,
            durationMs: read.durationMs ?? (await probeDurationMs(file)),
          };
          return {
            file,
            timing,
            startMs: startOf(file, timing),
            fromFile: timing.recordedAt !== null,
          };
        }),
      );
      // Recording order; "Lecture 2" before "Lecture 10" when times tie.
      probed.sort(
        (a, b) =>
          a.startMs - b.startMs ||
          a.file.name.localeCompare(b.file.name, undefined, { numeric: true }),
      );
      // Dismissed while the files were being read.
      if (batchToken.current !== token) return;
      setBatch((current) =>
        current === null ? null : { ...current, files: probed, probing: false },
      );
    },
    [batch, runUpload, status],
  );

  const resolveBatch = useCallback(
    async (choice: BatchChoice | null) => {
      const pending = batch;
      if (pending === null) return;
      if (choice !== null && pending.probing) return;
      batchToken.current += 1;
      setBatch(null);
      if (choice === null) return;
      if (choice === "join") {
        await runJoin(pending.files, pending.lessonId);
        return;
      }
      const count = pending.files.length;
      for (const [index, item] of pending.files.entries()) {
        const stored = await runUpload(item.file, pending.lessonId, {
          part: { index, count },
          last: index === count - 1,
          timing: item.timing,
        });
        // A failed or cancelled part stops the rest; its error is on screen.
        if (!stored) return;
      }
    },
    [batch, runJoin, runUpload],
  );

  const stop = useCallback(async () => {
    const recorder = mediaRecorder.current;
    const id = recordingId;
    if (recorder === null || id === null) return;
    mediaRecorder.current = null;
    setStatus("uploading");

    if (recorder.state !== "inactive") recorder.stop();
    await stopped.current;
    releaseDevice();

    // The final `dataavailable` fires before `onstop`; waiting on the write
    // chain guarantees it is in IndexedDB before finalize goes out.
    await writes.current;

    const durationMs = Date.now() - startedAt.current;
    setElapsedMs(durationMs);
    await patchSession(id, { stopRequested: true });
    try {
      await markStopped({ id, durationMs });
    } catch {
      // Metadata only - the bytes still upload and ingest sets `stored`.
    }
    uploader.current?.requestFinalize();
  }, [markStopped, recordingId, releaseDevice]);
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  const cancel = useCallback(async () => {
    extraction.current?.abort();
    extraction.current = null;
    const recorder = mediaRecorder.current;
    const id = recordingId;
    if (recorder !== null && recorder.state !== "inactive") recorder.stop();
    releaseDevice();
    uploader.current?.dispose();
    uploader.current = null;
    // Stops a batch from going on to its next part.
    settle.current?.(false);
    if (id !== null) {
      await clearRecording(id);
      await removeRecording({ id }).catch(() =>
        markFailed({ id, error: "Discarded by the user." }).catch(() => undefined),
      );
    }
    setStatus("idle");
    setRecordingId(null);
    setLessonId(null);
    setPending(0);
    setElapsedMs(0);
    setConnection("idle");
    setMode("capture");
    setUpload(null);
  }, [markFailed, recordingId, releaseDevice, removeRecording]);

  const reset = useCallback(() => {
    if (isBusy(status)) return;
    uploader.current?.dispose();
    uploader.current = null;
    setStatus("idle");
    setRecordingId(null);
    setLessonId(null);
    setPending(0);
    setElapsedMs(0);
    setError(null);
    setPartial(false);
    setConnection("idle");
    setMode("capture");
    setUpload(null);
    void wakeLock.current.disable();
  }, [status]);

  // Elapsed time ticks only while capturing; the upload can outlive it.
  useEffect(() => {
    if (status !== "recording") return;
    const timer = setInterval(() => setElapsedMs(Date.now() - startedAt.current), 500);
    return () => clearInterval(timer);
  }, [status]);

  // Closing the tab mid-recording loses the un-flushed MediaRecorder buffer,
  // and mid-upload it just delays things - both are worth a confirmation.
  useEffect(() => {
    if (!isBusy(status)) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [status]);

  const value = useMemo<RecorderApi>(
    () => ({
      status,
      recordingId,
      lessonId,
      pending,
      connection,
      elapsedMs,
      error,
      partial,
      source,
      setSource,
      systemAudioSupported,
      mode,
      upload,
      batch,
      start,
      uploadFiles,
      resolveBatch,
      stop,
      cancel,
      reset,
    }),
    [
      batch,
      cancel,
      connection,
      elapsedMs,
      error,
      lessonId,
      mode,
      partial,
      pending,
      recordingId,
      reset,
      resolveBatch,
      setSource,
      source,
      start,
      status,
      stop,
      systemAudioSupported,
      upload,
      uploadFiles,
    ],
  );

  return <RecorderContext.Provider value={value}>{children}</RecorderContext.Provider>;
}
